<template>
    <b-card no-body v-if="ready" class="card-container">
        <template v-slot:header>
            <div class="justify-content-between align-items-center">
                <b-row class="mb-2 mt-2">
                    <b-col md="6">
                        <b-input-group class="search-widget">
                            <template v-slot:prepend>
                                <b-input-group-text>
                                    <font-awesome-icon icon="search"></font-awesome-icon>
                                </b-input-group-text>
                            </template>
                            <b-form-input
                                type="search"
                                :placeholder="$t('search_po')"
                                v-model="filters.query"
                            ></b-form-input>
                            <template v-slot:append>
                                <b-input-group-append>
                                    <b-button
                                        variant="outline-secondary"
                                        @click="showFilters = !showFilters"
                                    >
                                        <font-awesome-icon icon="sliders-h"></font-awesome-icon>
                                        {{ $t("advanced_search") }}
                                    </b-button>
                                </b-input-group-append>
                            </template>
                        </b-input-group>

                        <div
                            class="purchase-orders-filters card p-3 bg-gradient-light mt-3"
                            v-show="showFilters"
                        >
                            <b-row>
                                <b-col>
                                    <b-form-select
                                        v-if="mode !== 'client' && clients.length > 1"
                                        v-model="filters.client_id"
                                        class="mb-2"
                                        :value="null"
                                        :options="clients"
                                    >
                                        <template v-slot:first>
                                            <option :value="null">
                                                {{ $t("clients") }}
                                            </option>
                                        </template>
                                    </b-form-select>

                                    <b-form-select
                                        v-if="mode !== 'manufacturer' && manufacturers.length >= 1"
                                        v-model="filters.manufacturer_id"
                                        class="mb-sm-0"
                                        :value="null"
                                        :options="manufacturers"
                                    >
                                        <template v-slot:first>
                                            <option :value="null">
                                                {{ $t("manufacturer") }}
                                            </option>
                                        </template>
                                    </b-form-select>
                                </b-col>
                                <b-col>
                                    <b-form-checkbox
                                        v-model="filters.has_invalid_refs"
                                        :unchecked-value="null"
                                    >
                                        {{ $t("invalid_refs") }}
                                    </b-form-checkbox>
                                    <b-form-checkbox-group
                                        v-model="filters.status"
                                        :options="statusOptions"
                                        stacked
                                    ></b-form-checkbox-group>
                                </b-col>
                            </b-row>
                        </div>
                    </b-col>

                    <b-col md="6" class="text-right" v-if="canCreateOrders">
                        <b-button :href="`/orders/new`" variant="primary" class="btn-round">
                            <font-awesome-icon :icon="['fas', 'plus']" />
                            {{ $t("create_new_order") }}
                        </b-button>
                    </b-col>
                </b-row>

                <orders-purchase-order
                    modal-id="new_purchase_order"
                    :admin-mode="isAdmin"
                    @saved="reload"
                ></orders-purchase-order>
            </div>
        </template>

        <b-card-body>
            <b-table
                striped
                hover
                responsive
                id="purchase-orders"
                ref="purchase-orders"
                :fields="fields"
                :items="items"
                :current-page="pagination.currentPage"
                :sort-by="sorting.sortBy"
                :sort-desc="sorting.desc"
                :busy="loading"
                show-empty=""
                :empty-text="$t('no_po_found')"
            >
                <template v-slot:empty="scope">
                    <div class="lead text-center py-3">
                        {{ scope.emptyText }}
                    </div>
                </template>

                <template v-slot:table-busy>
                    <div class="text-center text-primary my-2">
                        <b-spinner class="align-middle"></b-spinner>
                        <strong>{{ $t("loading") }}</strong>
                    </div>
                </template>

                <template v-slot:cell(po_number)="data">
                    <div class="d-sm-flex justify-content-sm-between">
                        <b-link :to="`/orders/${data.item.id}/products`">
                            {{ data.item.po_number }}
                        </b-link>
                        <span
                            v-if="data.item.invalid_refs"
                            v-b-tooltip.hover
                            :title="
                                $t('invalid_refs_tooltip', {
                                    invalid_refs: data.item.invalid_refs,
                                })
                            "
                        >
                            <font-awesome-icon
                                icon="exclamation-triangle"
                                class="text-danger"
                            ></font-awesome-icon
                        ></span>
                    </div>
                </template>

                <template v-slot:cell(status)="data">
                    <b-badge
                        v-if="data.item.status === 'sent'"
                        variant="success"
                        class="text-white"
                        v-b-tooltip.hover
                        :title="
                            $t('sent_on', {
                                date: $moment
                                    .utc(data.item.date_sent)
                                    .local()
                                    .format('L LTS'),
                            })
                        "
                        >✔ {{ $t("sent") }}</b-badge
                    >
                    <b-badge v-else variant="info">{{ data.item.status }}</b-badge>
                </template>

                <template v-slot:cell(order_date)="data">
                    {{ formatDate(data.item.order_date) }}
                </template>

                <template v-slot:cell(due_date)="data">
                    {{ formatDate(data.item.due_date) }}
                </template>

                <template v-slot:cell(clientManufacturer)="data">
                    <div v-if="mode !== 'client'">{{ data.item.client.name }}</div>
                    <div v-if="mode !== 'manufacturer'">
                        {{ data.item.manufacturer.name }}
                    </div>
                </template>

                <template v-slot:cell(total_quantity)="data">
                    <strong>{{ data.item.total_quantity.toLocaleString() }}</strong
                    ><br />
                    <b class="small text-info">{{
                        $t("completed", {
                            percentage: calculateCompletedPercentage(data.item),
                        })
                    }}</b>
                </template>

                <template v-slot:cell(actions)="data">
                    <div class="text-nowrap">
                        <b-button-toolbar key-nav justify style="flex-wrap: nowrap">

                            <b-button
                                v-if="data.item.status === 'sent'"
                                :href="`/api/orders/${data.item.id}/download-csv`"
                                variant="outline-primary"
                                size="sm"
                                class="btn-lte"
                                v-b-tooltip.hover="'Download as CSV'"
                            >
                                <font-awesome-icon :icon="['fas', 'download']" />
                            </b-button>

                            <b-button
                                v-if="data.item.status === 'sent' && data.item.is_skip_quality_control"
                                @click="downloadQRCodes(data.item.id)"
                                variant="outline-primary"
                                size="sm"
                                class="btn-lte"
                                v-b-tooltip.hover="'Download Passports Qr Codes'"
                            >
                                <font-awesome-icon :icon="['fas', 'qrcode']" />
                            </b-button>

                            <orders-send-dialog
                                @success="showSuccess"
                                @error="showError"
                                :order="data.item"
                                v-if="data.item.status === 'draft' && (me.type === 'client' || isAdmin)"
                                :button-style="{ variant: 'outline-primary', size: 'sm' }"
                                :title="`Send ${data.item.po_number}`"
                            ></orders-send-dialog>

                            <delete-button
                                @success="deleteItem(data.item)"
                                v-if="data.item.status === 'draft' && (me.type === 'client' || isAdmin)"
                                class="ml-2 actions-danger"
                                type="Purchase order"
                                :title="`Delete ${data.item.po_number}`"
                            ></delete-button>

                        </b-button-toolbar>
                    </div>
                </template>
            </b-table>

            <b-modal
                id="modal-error"
                size="sm"
                title="Something went wrong :("
                hide-footer
            >
                <template>
                    <div class="d-flex justify-content-between flex-column">
                        <h6>{{ errorMessage }}</h6>
                        <b-button v-if="showUnlockButton" @click="unlockPassports"
                            >Unlock Passports</b-button
                        >
                    </div>
                </template>
            </b-modal>

            <b-modal id="modal-success" size="sm" title="Success" hide-footer>
                <template>
                    <div class="d-flex justify-content-between flex-column">
                        <h6>{{ successMessage }}</h6>
                    </div>
                </template>
            </b-modal>

            <b-pagination
                v-if="pagination.lastPage > 1"
                v-model="pagination.currentPage"
                :total-rows="pagination.total"
                :per-page="pagination.perPage"
                aria-controls="my-table"
            ></b-pagination>
        </b-card-body>

        <b-card-footer>
            <div class="text-center" v-if="pagination.total > 1">
                {{
                    $t("showing_x_results", {
                        current: pagination.to - pagination.from + 1,
                        total: pagination.total,
                    })
                }}
                <span v-if="hasFilter">
                    · {{ $t("results_filtered") }} ·
                    <b-link @click="resetFilters()">{{
                        $t("clear_all")
                    }}</b-link></span
                >
            </div>
        </b-card-footer>
    </b-card>
</template>

<script>
import { mapActions, mapState } from "vuex";
import _ from "lodash";
import axios from "axios";
import deleteButton from "../Common/DeleteButton";
import calculateCompletedPercentage from "../../plugins/mixins";

export default {
  name: "PurchaseOrderList",
  components: { deleteButton },
  mixins: [calculateCompletedPercentage],
  props: {
    isAdmin: {
      type: Boolean,
      default: false
    },
    mode: {
      type: String,
      required: true
    },
    canCreateOrders: {
      type: Boolean,
      default: false
    }
  },
  data() {
    let fields = this.getFields();
    return {
      fields: fields,
      items: this.getItems,
      manufacturers: [],
      clients: [],
      showFilters: false,
      loadingRows: [],
      filters: {
        query: null,
        manufacturer_id: null,
        client_id: null,
        has_invalid_refs: null,
        status: []
      },
      statusOptions: [
        { text: this.$t("draft"), value: "draft" },
        { text: this.$t("Sent"), value: "sent" }
      ],
      ready: false,
      errorMessage: "",
      successMessage: "",
      showUnlockButton: false
    };
  },
  computed: {
    ...mapState({
      orders: state => state.purchaseOrders.orders,
      pagination: state => state.purchaseOrders.pagination,
      sorting: state => state.purchaseOrders.sorting,
      loading: state => state.purchaseOrders.loading,
      me: state => state.me.me
    }),
    hasFilter() {
      const isEmpty = Object.values(this.filters).every(
        x => x === null || x === "" || x.length === 0
      );
      return !isEmpty;
    }
  },
  watch: {
    "filters.client_id": {
      immediate: false, // ou true si vous voulez appeler getManufacturers dès le montage
      handler(newVal, oldVal) {
        // Quand le client change, on remet le manufacturer sélectionné à null
        this.filters.manufacturer_id = null;

        // On recharge la liste des manufacturers pour ce client
        this.getManufacturers();
      }
    },
    filters: {
      handler: _.debounce(function() {
        this.updatePagination(1);
        this.reload();
      }, 500),
      deep: true
    }
  },
  mounted() {
    this.getMe().then(r => {
      this.ready = true;
      this.loadResources();
    });
  },
  methods: {
    showSuccess(data) {
      this.successMessage = data.message;
      this.$bvModal.show("modal-success");

      this.reload();

      setInterval(() => this.$bvModal.hide("modal-success"), 2000);
    },
    showError(data) {
      this.errorMessage = data.message;

      if (data.errorCode == 1) {
        this.showUnlockButton = true;
      } else {
        this.showUnlockButton = false;
      }

      this.$bvModal.show("modal-error");
    },
    unlockPassports() {
      window.open("https://www.ionaute.com/", "_blank");
    },
    deleteItem(item) {
      this.$set(this.loadingRows, item.index, true);
      this.deleteOrder(item.item.id).finally(() => {
        this.reload();
        this.$set(this.loadingRows, item.index, false);
      });
    },
    loadResources() {
      if (this.mode !== "client") {
        this.getClients();
      }

      if (this.mode !== "manufacturer") {
        this.getManufacturers();
      }
    },
    getItems(ctx, cb) {
      this.updatePagination(ctx.currentPage);
      this.updateSorting({ sortBy: ctx.sortBy, desc: ctx.sortDesc });
      this.updateFilters(this.filters);
      this.getOrders().then(data => {
        cb(this.orders);
      });
      return null;
    },
    getClients() {
      if (this.me.type === "client") {
        return;
      }

      let url =
        this.me.type === "manufacturer"
          ? `/api/manufacturers/${this.me.company.id}/clients`
          : "/api/clients";

      return axios.get(url).then(r => {
        this.clients = r.data.data.map(c => {
          return {
            value: c.id,
            text: c.name
          };
        });
      });
    },
    getManufacturers() {
      // Si un client est sélectionné, on charge seulement ses manufacturers
      if (this.filters.client_id) {
        return axios
          .get(`/api/clients/${this.filters.client_id}/manufacturers`, {
            params: { noPagination: 1 }
          })
          .then(r => {
            this.manufacturers = r.data.data.map(m => ({
              value: m.id,
              text: m.name
            }));
          });
      }
      // Si aucun client n’est sélectionné, on revient à la logique existante
      else if (this.isAdmin) {
        return axios
          .get("/api/manufacturers", { params: { noPagination: 1 } })
          .then(r => {
            this.manufacturers = r.data.data.map(m => ({
              value: m.id,
              text: m.name
            }));
          });
      } else {
        // Par exemple si l’utilisateur est "client" ou "manufacturer" et n'a pas choisi de client
        return axios
          .get(`/api/clients/${this.me.company.id}/manufacturers`, {
            params: { noPagination: 1 }
          })
          .then(r => {
            this.manufacturers = r.data.data.map(m => ({
              value: m.id,
              text: m.name
            }));
          });
      }
    },
    reload() {
      this.$root.$emit("bv::refresh::table", "purchase-orders");
    },
    formatDate(dateString) {
      if (!dateString) return "";
      const date = new Date(dateString);
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0"); // Les mois commencent à 0
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    },
    getFields() {
      let fields = [
        {
          key: "po_number",
          sortable: true,
          label: this.$t("po_number"),
          class: "text-nowrap align-middle"
        },
        {
          key: "status",
          sortable: true,
          label: this.$t("status"),
          class: "text-capitalize align-middle"
        },
        {
          key: "order_date",
          sortable: true,
          label: this.$t("order_date"),
          class: "text-nowrap align-middle"
        },
        {
          key: "due_date",
          sortable: true,
          label: this.$t("due_date"),
          class: "text-nowrap align-middle"
        }
      ];

      let modeHeading = [];
      if (this.mode !== "client") {
        modeHeading.push(this.$t("client"));
      }
      if (this.mode !== "manufacturer") {
        modeHeading.push(this.$t("manufacturer"));
      }
      fields.push({
        key: "clientManufacturer",
        sortable: true,
        label: modeHeading.join(" / "),
        class: "align-middle"
      });

      fields.push(
        ...[
          {
            key: "total_quantity",
            sortable: true,
            label: this.$t("quantity"),
            class: "text-right"
          },
          {
            key: "actions",
            sortable: false,
            label: this.$t("actions"),
            class: "align-middle"
          }
        ]
      );
      return fields;
    },
    resetFilters() {
      // @todo: this is not elegant. should use a mutation to get the default state instead
      this.filters.manufacturer_id = null;
      this.filters.client_id = null;
      this.filters.has_invalid_refs = null;
      this.filters.status = [];
    },
    async downloadQRCodes(orderId) {
      try {
        const response = await axios.post(
          "/api/passports/download-qr-codes",
          {
            type: "purchase-order",
            id: orderId
          },
          {
            responseType: "blob" // Important pour les fichiers
          }
        );

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `qr-codes-order-${orderId}.zip`);
        document.body.appendChild(link);
        link.click();
      } catch (error) {}
    },
    ...mapActions({
      getOrders: "purchaseOrders/getOrders",
      deleteOrder: "purchaseOrders/deleteOrder",
      updatePagination: "purchaseOrders/updatePagination",
      updateSorting: "purchaseOrders/updateSorting",
      updateFilters: "purchaseOrders/updateFilters",
      getMe: "me/getMe"
    })
  }
};
</script>
